import React, { useEffect, useState, useCallback } from "react";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import TableColumn from "../../components/TableColumn/TableColumn";
import api from "../../services/api";
import { format } from "date-fns";
import "./PushNotifications.scss";

const PushNotifications = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(
    Number(localStorage.getItem("ListCurrentPagePushNotifications")) || 1
  );

  const records = 10;
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState("desc");

  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    return format(new Date(timestamp), "MMM d, yyyy h:mm a");
  };

  const getTypeLabel = (type) => {
    const labels = {
      ai_trigger: "AI Trigger",
      event: "Event",
      perk: "Perk / Offer",
      spotlight: "Spotlight",
      bar_crawl: "Bar Crawl",
      chat: "Chat",
      promotional: "Promotional",
      profile: "Profile",
      ticket_purchased: "Ticket",
      general: "General",
    };
    return labels[type] || type || "General";
  };

  const getCampaigns = useCallback(
    async (page) => {
      try {
        const response = await api.get(`/push-notification-admin/campaigns`, {
          params: {
            page,
            records,
            search: searchTerm,
            sort: sortDirection,
          },
        });

        const items = response.data.data.map((c) => ({
          Title: c.title || "—",
          Message:
            c.message && c.message.length > 60
              ? c.message.substring(0, 60) + "..."
              : c.message || "—",
          Type: (
            <span className="type-badge">{getTypeLabel(c.type)}</span>
          ),
          "Date Sent": formatDate(c.sent_at),
          Recipients: c.total_recipients,
          Opened: c.total_opened,
          "Open Rate": (
            <span
              className={`open-rate ${
                c.open_rate >= 50
                  ? "high"
                  : c.open_rate >= 20
                  ? "medium"
                  : "low"
              }`}
            >
              {c.open_rate}%
            </span>
          ),
          id: c.group_key,
        }));

        setCampaigns(items);
        setTotalPages(response.data.total);
      } catch (error) {
        console.error("Error fetching push notification campaigns:", error);
      }
    },
    [searchTerm, sortDirection]
  );

  useEffect(() => {
    getCampaigns(currentPage);
  }, [currentPage, sortDirection, searchTerm, getCampaigns]);

  const fetchAllData = async () => {
    try {
      const response = await api.get(`/push-notification-admin/campaigns`, {
        params: { records: 9999999999 },
      });

      return response.data.data.map((c) => ({
        Title: c.title || "—",
        Message: c.message || "—",
        Type: getTypeLabel(c.type),
        "Date Sent": formatDate(c.sent_at),
        Recipients: c.total_recipients,
        Opened: c.total_opened,
        "Open Rate": `${c.open_rate}%`,
        id: c.group_key,
      }));
    } catch (error) {
      console.error("Error fetching all data:", error);
      return [];
    }
  };

  const columns = [
    "Title",
    "Message",
    "Type",
    "Date Sent",
    "Recipients",
    "Opened",
    "Open Rate",
  ];

  const sortableColumns = ["Date Sent"];

  const actions = [
    {
      label: <MdOutlineRemoveRedEye size={30} color="#4F30EA" />,
      onClick: (rowData) => {
        navigate(
          `/push-notifications/${encodeURIComponent(rowData.id)}`
        );
      },
    },
  ];

  const handleChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    localStorage.setItem("ListCurrentPagePushNotifications", page);
  };

  useEffect(() => {
    const storedPage = localStorage.getItem("ListCurrentPagePushNotifications");
    setCurrentPage(Number(storedPage) || 1);
  }, []);

  const handleSort = () => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
  };

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content" style={{ padding: 24 }}>
          <section className="push-notifications">
            <TableColumn
              title="Push Notifications"
              hasHeader={true}
              columns={columns}
              data={campaigns}
              sortableColumns={sortableColumns}
              hasExport={true}
              hasSearch={true}
              actions={actions}
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              records={records}
              inputSearchProps={{ value: searchTerm, onChange: handleChange }}
              fetchAllData={fetchAllData}
              onSort={handleSort}
            />
          </section>
        </article>
      </div>
    </main>
  );
};

export default PushNotifications;
